import type {
  CollisionDetection,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  getClientRect,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { Stars01 } from '@untitledui/icons';
import DOMPurify from 'dompurify';
import type { Ref } from 'react';
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';

import { useEscapeToBlur } from '@/hooks/useEscapeToBlur';
import { Badge } from '@/lib/base/badges/badges';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import type { PlateEmailEditorHandle } from '@/lib/reasoning/components/email-editor';
import { htmlToPlainText } from '@/lib/reasoning/components/email-editor/plateEmailSerializer';
import { cx } from '@/utils/cx';
import { shouldIgnoreOutsideInteraction } from '@/utils/third-party-editor-ui';

import PlateEmailEditor from '../reasoning/PlateEmailEditor';
import CannedResponsesMenu from './canned-responses/CannedResponsesMenu';
import EmailRecipientsInput from './EmailRecipientsInput';
import { ResizableEmailBody } from './ResizableEmailBody';

// A user who typed/clicked within this window is still "editing" even if the
// editor has lost focus — external content must not be applied (and the editor
// must never remount) during an active editing session.
const EDITING_IDLE_GRACE_MS = 5000;

// Focus can bounce between fields (subject → body → recipient chip) within the
// same editing session; only treat focus leaving the whole editor as a session
// end after this settle delay.
const SESSION_END_SETTLE_MS = 200;

/** Connected email account for multi-account OAuth */
export interface ConnectedAccount {
  id: number;
  email: string;
  is_primary: boolean;
}

/** Latest header values, as emitted by the debounced header autosave. */
export interface EmailHeaderSnapshot {
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
}

/** External (server/AI) content applied in place — see `applyExternalContent`. */
export interface ExternalEmailContent {
  to?: string[];
  cc?: string[];
  bcc?: string[];
  subject?: string;
  body_html?: string;
  body?: string;
}

/**
 * Imperative handle for pulling the editor's latest header values synchronously.
 * Header edits (recipients/subject) propagate to the parent through a 500ms
 * debounce; a consumer that must act on the current values immediately (e.g.
 * dispatching a send the instant the user clicks the button) calls
 * `flushHeaders()` to cancel the pending debounce, push the current values to
 * `onContentChange`, and receive them synchronously.
 */
export interface EmailEditorHandle {
  flushHeaders: () => EmailHeaderSnapshot;
  /**
   * Whether the user is in an active editing session: focus is inside the
   * editor, OR they typed/clicked within the last EDITING_IDLE_GRACE_MS.
   * Parents must never apply external content (or remount) while this is true.
   */
  isUserEditing: () => boolean;
  /**
   * Apply external (server/AI) content in place — headers via state, body via
   * PlateEmailEditor.replaceContent. Never remounts, never emits
   * onContentChange (external content must not round-trip as a user edit).
   */
  applyExternalContent: (content: ExternalEmailContent) => void;
  /**
   * Remove the draft's signature block via the live editor — the same Plate
   * transform as the block's own remove button, so the change flows through
   * onContentChange and the surface reconciles the disposition to
   * `'user_removed'`. Returns whether a signature block was found and removed.
   */
  removeSignature: () => boolean;
}

interface EmailEditorProps {
  /** Imperative handle exposing flushHeaders / isUserEditing / applyExternalContent (React 19 ref-as-prop). */
  ref?: Ref<EmailEditorHandle>;

  // Email content data
  to?: string | string[];
  cc?: string | string[];
  bcc?: string | string[];
  subject?: string;
  bodyHtml?: string;
  body?: string;

  // Callbacks
  onContentChange: (content: {
    to?: string[];
    cc?: string[];
    bcc?: string[];
    subject?: string;
    body_html?: string;
    body?: string;
    social_account_id?: number;
    disable_tracking?: boolean;
  }) => void;

  // Configuration
  editable?: boolean;
  showGmailBadge?: boolean;
  saveStatus?: 'idle' | 'pending' | 'saving' | 'saved' | 'error';

  // Thread viewing
  threadId?: string;
  onViewThread?: () => void;

  // Version history
  versionCount?: number;
  onShowVersionHistory?: () => void;

  // Email details (for read-only display)
  emailDetails?: {
    to_addresses?: string[];
    cc_addresses?: string[];
    bcc_addresses?: string[];
  };

  // Multi-account OAuth support
  /** Connected email accounts (show "From" selector if > 1) */
  connectedAccounts?: ConnectedAccount[];
  /** Currently selected account ID */
  selectedAccountId?: number;
  /** Callback when From account changes */
  onFromChange?: (accountId: number) => void;

  /** Organization ID for resolving file:ID URLs in images */
  orgId?: number;

  /**
   * Meeting-slot ids referenced in this email's body, pre-resolved to their
   * start/end times (keyed by slot id) — e.g. `action.enriched_data.resolved_slots`.
   * Threaded straight through to PlateEmailEditor's `resolvedSlots` prop; see
   * that prop's doc for the resolution behavior.
   */
  resolvedSlots?: Record<string, { start: string; end: string; label: string }>;

  /** Callback when files are attached via UI (e.g., attach button) */
  onAttachFile?: (files: File[]) => void;

  /** Whether file attachment upload is in progress */
  isAttaching?: boolean;

  /** Whether this email is a reply to an existing thread */
  isReply?: boolean;

  /**
   * When true, strips outer card chrome (border, shadow, rounded corners, mt-2)
   * and hides the "Drafted Message" badge and Gmail badge.
   * Use when EmailEditor is embedded inside ActionAccordion.
   */
  embedded?: boolean;

  /**
   * When true, the recipient header (To/Cc/Bcc/Subject) opens in edit mode
   * instead of the collapsed summary. Used by the compose window so the user
   * lands directly in the editable fields; proposal cards leave this false and
   * expand on click.
   */
  initialHeaderEditing?: boolean;

  /**
   * When true, resolved recipient chips become clickable and open the CRM
   * slideout for that person. Opt-in (the compose window) so proposal cards
   * keep plain recipient chips. Requires `orgId`.
   */
  linkRecipientsToRecords?: boolean;

  /**
   * When set, constrains the email body to this height with overflow-y scrolling.
   * Prevents long emails from dominating the proposal view. E.g. '300px'.
   */
  maxBodyHeight?: string;

  /** When provided, renders a Preview button that opens the composed-email preview modal. */
  onPreview?: () => void;

  /**
   * Fired once when focus leaves the editor entirely (after a short settle
   * delay so field-to-field focus moves don't count). Parents use this to
   * apply external content that was deferred while the user was editing.
   */
  onEditingSessionEnd?: () => void;
}

// ─── Pure helpers (module scope) ────────────────────────────────────────────

/** Convert string | string[] to string[] */
function normalizeToArray(value: string | string[] | undefined): string[] {
  if (!value) {
    return [];
  }
  if (Array.isArray(value)) {
    return value;
  }
  return value
    .split(',')
    .map(email => email.trim())
    .filter(Boolean);
}

/** Check if email field has values */
function hasEmailAddresses(
  detailAddresses?: string[],
  contentAddresses?: string | string[]
): boolean {
  if (detailAddresses && detailAddresses.length > 0) {
    return true;
  }
  if (Array.isArray(contentAddresses)) {
    return contentAddresses.length > 0;
  }
  if (typeof contentAddresses === 'string') {
    return contentAddresses.length > 0;
  }
  return false;
}

/** Convert field id to display label */
function getFieldLabel(fieldId: 'to' | 'cc' | 'bcc' | null): string {
  if (fieldId === 'to') {
    return 'To';
  }
  if (fieldId === 'cc') {
    return 'Cc';
  }
  if (fieldId === 'bcc') {
    return 'Bcc';
  }
  return 'unknown';
}

/** Decode HTML entities in a string */
function decodeHtmlEntities(text: string): string {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
}

/** Format email addresses for display */
function formatEmailAddresses(
  addresses: string | string[] | undefined
): string {
  if (!addresses) {
    return 'N/A';
  }
  if (Array.isArray(addresses)) {
    return decodeHtmlEntities(addresses.join(', '));
  }
  return decodeHtmlEntities(addresses);
}

/** Derive a display name from an email address (e.g. "john.smith@co.com" → "John Smith") */
function emailToDisplayName(email: string): string {
  const local = email.split('@')[0];
  return local
    .split(/[._-]/)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/** Render To addresses as "Display Name <email>" with styled email */
function renderToAddresses(addresses: string | string[] | undefined) {
  if (!addresses) {
    return <span>N/A</span>;
  }
  const list = normalizeToArray(addresses);
  return list.map((addr, i) => {
    const decoded = decodeHtmlEntities(addr.trim());
    const displayName = emailToDisplayName(decoded);
    return (
      <span key={decoded}>
        {i > 0 && ', '}
        {displayName} <span className='text-[#A4A7AE]'>&lt;{decoded}&gt;</span>
      </span>
    );
  });
}

// ─── Component ──────────────────────────────────────────────────────────────

function EmailEditor({
  to,
  cc,
  bcc,
  subject,
  bodyHtml,
  body,
  onContentChange,
  editable = false,
  showGmailBadge = true,
  saveStatus = 'idle',
  threadId,
  onViewThread,
  versionCount,
  onShowVersionHistory,
  emailDetails,
  connectedAccounts,
  selectedAccountId,
  onFromChange,
  orgId,
  onAttachFile,
  isAttaching,
  resolvedSlots,
  isReply = false,
  embedded = false,
  initialHeaderEditing = false,
  linkRecipientsToRecords = false,
  maxBodyHeight,
  onPreview,
  onEditingSessionEnd,
  ref,
}: EmailEditorProps) {
  // Header editing state
  const [isEditingHeader, setIsEditingHeader] = useState(initialHeaderEditing);
  const headerRef = useRef<HTMLDivElement>(null);

  // ── Editing-session tracking ────────────────────────────────────────────
  // Root container ref: focus-within + input-recency define "user is
  // editing". Ref writes only — no re-renders per keystroke.
  const containerRef = useRef<HTMLDivElement>(null);
  const lastInputAtRef = useRef(0);
  const sessionEndTimerRef = useRef<NodeJS.Timeout | null>(null);
  const plateRef = useRef<PlateEmailEditorHandle>(null);
  // Keep the (possibly unstable) callback prop out of effect/handler deps.
  const onEditingSessionEndRef = useRef(onEditingSessionEnd);
  onEditingSessionEndRef.current = onEditingSessionEnd;

  // Has the user authored anything in the BODY since mount? Narrower than
  // `lastInputAtRef` on two axes, both deliberate:
  //   • only keystrokes/input, not pointer-downs — placing a caret is
  //     engagement but produces no content;
  //   • only the body subtree, not the headers — see the handler wiring.
  // This is what tells a genuine body edit apart from Plate's mount-time
  // normalization in `handleBodyChange`.
  const userTypedInBodyRef = useRef(false);

  const markUserInput = useCallback(() => {
    lastInputAtRef.current = Date.now();
  }, []);

  const markUserTyped = useCallback(() => {
    lastInputAtRef.current = Date.now();
    userTypedInBodyRef.current = true;
  }, []);

  const isUserEditingNow = useCallback(() => {
    const focusWithin =
      containerRef.current?.contains(document.activeElement) ?? false;
    const recentInput =
      Date.now() - lastInputAtRef.current < EDITING_IDLE_GRACE_MS;
    return focusWithin || recentInput;
  }, []);

  const handleContainerFocus = useCallback(() => {
    // Focus returned (or bounced between fields) — cancel any pending
    // session-end notification.
    if (sessionEndTimerRef.current) {
      clearTimeout(sessionEndTimerRef.current);
      sessionEndTimerRef.current = null;
    }
  }, []);

  const handleContainerBlur = useCallback(() => {
    if (sessionEndTimerRef.current) {
      clearTimeout(sessionEndTimerRef.current);
    }
    sessionEndTimerRef.current = setTimeout(() => {
      sessionEndTimerRef.current = null;
      const stillWithin =
        containerRef.current?.contains(document.activeElement) ?? false;
      if (!stillWithin) {
        onEditingSessionEndRef.current?.();
      }
    }, SESSION_END_SETTLE_MS);
  }, []);

  // Cleanup session-end timer on unmount
  useEffect(() => {
    return () => {
      if (sessionEndTimerRef.current) {
        clearTimeout(sessionEndTimerRef.current);
      }
    };
  }, []);
  // Local email state
  const [localTo, setLocalTo] = useState<string[]>(() =>
    normalizeToArray(emailDetails?.to_addresses || to)
  );
  const [localCc, setLocalCc] = useState<string[]>(() =>
    normalizeToArray(emailDetails?.cc_addresses || cc)
  );
  const [localBcc, setLocalBcc] = useState<string[]>(() =>
    normalizeToArray(emailDetails?.bcc_addresses || bcc)
  );
  const [localSubject, setLocalSubject] = useState(subject || '');

  // Cc/Bcc visibility state - persists across collapse/expand
  const [showCc, setShowCc] = useState(() =>
    hasEmailAddresses(emailDetails?.cc_addresses, cc)
  );
  const [showBcc, setShowBcc] = useState(() =>
    hasEmailAddresses(emailDetails?.bcc_addresses, bcc)
  );

  // Drag-and-drop state for cross-field email movement
  const [activeEmail, setActiveEmail] = useState<string | null>(null);
  const [activeField, setActiveField] = useState<'to' | 'cc' | 'bcc' | null>(
    null
  );
  const [overField, setOverField] = useState<'to' | 'cc' | 'bcc' | null>(null);
  // Track original Cc/Bcc visibility to restore after drag
  const [preDropShowCc, setPreDropShowCc] = useState(false);
  const [preDropShowBcc, setPreDropShowBcc] = useState(false);
  // Aria-live announcement for duplicate prevention
  const [announcement, setAnnouncement] = useState('');
  const announcementTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Configure sensors with touch support
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Custom collision detection that prioritizes droppable field containers over sortable items
  // This ensures cross-field dragging works reliably, especially for empty fields
  const customCollisionDetection: CollisionDetection = useCallback(args => {
    // First check if pointer is within any droppable field container (to, cc, bcc)
    const pointerCollisions = pointerWithin(args);
    const fieldIds = ['to', 'cc', 'bcc'];

    // Find field container collisions (prioritize these for cross-field drops)
    const fieldCollision = pointerCollisions.find(collision =>
      fieldIds.includes(collision.id as string)
    );

    if (fieldCollision) {
      // If we're over a field container, return it as the primary collision
      // This enables dropping into empty fields and cross-field movement
      return [fieldCollision];
    }

    // Fall back to closestCenter for sortable item reordering within a field
    return closestCenter(args);
  }, []);

  // Determine which field contains the given email
  const findEmailField = useCallback(
    (email: string): 'to' | 'cc' | 'bcc' | null => {
      if (localTo.includes(email)) {
        return 'to';
      }
      if (localCc.includes(email)) {
        return 'cc';
      }
      if (localBcc.includes(email)) {
        return 'bcc';
      }
      return null;
    },
    [localTo, localCc, localBcc]
  );

  // Get field array by field ID
  const getFieldArray = useCallback(
    (fieldId: 'to' | 'cc' | 'bcc'): string[] => {
      if (fieldId === 'to') {
        return localTo;
      }
      if (fieldId === 'cc') {
        return localCc;
      }
      return localBcc;
    },
    [localTo, localCc, localBcc]
  );

  // Set field array by field ID
  const setFieldArray = useCallback(
    (fieldId: 'to' | 'cc' | 'bcc', value: string[]) => {
      if (fieldId === 'to') {
        setLocalTo(value);
      } else if (fieldId === 'cc') {
        setLocalCc(value);
      } else {
        setLocalBcc(value);
      }
    },
    []
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const email = event.active.id as string;
      const field = findEmailField(email);
      setActiveEmail(email);
      setActiveField(field);
      // Save current visibility state
      setPreDropShowCc(showCc);
      setPreDropShowBcc(showBcc);
      // Reveal all drop targets
      setShowCc(true);
      setShowBcc(true);
    },
    [findEmailField, showCc, showBcc]
  );

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { over } = event;
      if (!over) {
        setOverField(null);
        return;
      }

      // Check if over a droppable field
      const overId = over.id as string;
      if (overId === 'to' || overId === 'cc' || overId === 'bcc') {
        setOverField(overId);
      } else {
        // Over an email chip - find which field it belongs to
        const field = findEmailField(overId);
        setOverField(field);
      }
    },
    [findEmailField]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || !activeField) {
        // No valid drop - restore original visibility
        setShowCc(preDropShowCc);
        setShowBcc(preDropShowBcc);
        setActiveEmail(null);
        setActiveField(null);
        setOverField(null);
        return;
      }

      const email = active.id as string;
      const overId = over.id as string;

      // Determine target field
      let targetField: 'to' | 'cc' | 'bcc';
      if (overId === 'to' || overId === 'cc' || overId === 'bcc') {
        targetField = overId;
      } else {
        // Over an email chip - find which field it belongs to
        const field = findEmailField(overId);
        if (!field) {
          setShowCc(preDropShowCc);
          setShowBcc(preDropShowBcc);
          setActiveEmail(null);
          setActiveField(null);
          setOverField(null);
          return;
        }
        targetField = field;
      }

      if (activeField === targetField) {
        // Same field - reorder within the field
        const fieldArray = getFieldArray(targetField);
        const oldIndex = fieldArray.indexOf(email);
        let newIndex = fieldArray.indexOf(overId);

        // If dropping on the field itself (not a chip), place at end
        if (overId === targetField) {
          newIndex = fieldArray.length - 1;
        }

        if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
          setFieldArray(targetField, arrayMove(fieldArray, oldIndex, newIndex));
        }
      } else {
        // Different field - move between fields
        const targetArray = getFieldArray(targetField);

        // Check for duplicate
        if (targetArray.includes(email)) {
          let fieldLabel = 'Bcc';
          if (targetField === 'to') {
            fieldLabel = 'To';
          } else if (targetField === 'cc') {
            fieldLabel = 'Cc';
          }
          setAnnouncement(
            `Cannot move – email already exists in ${fieldLabel} field`
          );
          // Clear announcement after a short delay
          if (announcementTimerRef.current) {
            clearTimeout(announcementTimerRef.current);
          }
          announcementTimerRef.current = setTimeout(
            () => setAnnouncement(''),
            3000
          );
          // Restore original visibility
          setShowCc(preDropShowCc);
          setShowBcc(preDropShowBcc);
          setActiveEmail(null);
          setActiveField(null);
          setOverField(null);
          return;
        }

        // Remove from source field
        const sourceArray = getFieldArray(activeField);
        const newSourceArray = sourceArray.filter(e => e !== email);
        setFieldArray(activeField, newSourceArray);

        // Add to target field at drop position
        let insertIndex = targetArray.length;
        if (overId !== targetField) {
          const overIndex = targetArray.indexOf(overId);
          if (overIndex !== -1) {
            insertIndex = overIndex;
          }
        }
        const newTargetArray = [...targetArray];
        newTargetArray.splice(insertIndex, 0, email);
        setFieldArray(targetField, newTargetArray);
      }

      // Restore Cc/Bcc visibility if empty after move
      let newLocalCc = getFieldArray('cc');
      if (activeField === 'cc' && targetField !== 'cc') {
        newLocalCc = getFieldArray('cc').filter(e => e !== email);
      } else if (targetField === 'cc') {
        newLocalCc = [...getFieldArray('cc'), email];
      }

      let newLocalBcc = getFieldArray('bcc');
      if (activeField === 'bcc' && targetField !== 'bcc') {
        newLocalBcc = getFieldArray('bcc').filter(e => e !== email);
      } else if (targetField === 'bcc') {
        newLocalBcc = [...getFieldArray('bcc'), email];
      }

      if (newLocalCc.length === 0) {
        setShowCc(preDropShowCc);
      }
      if (newLocalBcc.length === 0) {
        setShowBcc(preDropShowBcc);
      }

      setActiveEmail(null);
      setActiveField(null);
      setOverField(null);
    },
    [
      activeField,
      findEmailField,
      getFieldArray,
      setFieldArray,
      preDropShowCc,
      preDropShowBcc,
    ]
  );

  const handleDragCancel = useCallback(() => {
    // Restore original visibility
    setShowCc(preDropShowCc);
    setShowBcc(preDropShowBcc);
    setActiveEmail(null);
    setActiveField(null);
    setOverField(null);
  }, [preDropShowCc, preDropShowBcc]);

  // Screen reader announcements for drag operations
  const dragAnnouncements = {
    onDragStart: ({ active }: DragStartEvent) => {
      const foundField = findEmailField(active.id as string);
      const field = getFieldLabel(foundField);
      return `Picked up ${active.id} from ${field} field. Use arrow keys to move between To, Cc, and Bcc fields. Press Space or Enter to drop.`;
    },
    onDragOver: ({ over }: DragOverEvent) => {
      if (!over) {
        return 'Not over a drop target';
      }
      const overId = over.id as string;
      let field: string;
      if (overId === 'to' || overId === 'cc' || overId === 'bcc') {
        field = getFieldLabel(overId);
      } else {
        const foundField = findEmailField(overId);
        field = getFieldLabel(foundField);
      }
      return `Over ${field} field. Release to drop here.`;
    },
    onDragEnd: ({ active, over }: DragEndEvent) => {
      if (!over) {
        return 'Drop cancelled';
      }
      const overId = over.id as string;
      let targetField: string;
      if (overId === 'to' || overId === 'cc' || overId === 'bcc') {
        targetField = getFieldLabel(overId);
      } else {
        const foundField = findEmailField(overId);
        targetField = getFieldLabel(foundField);
      }
      return `Moved ${active.id} to ${targetField} field.`;
    },
    onDragCancel: () => 'Drag cancelled. Email returned to original position.',
  };

  // Multi-account: show From selector only when multiple accounts exist
  const showFromSelector = connectedAccounts && connectedAccounts.length > 1;

  // Get selected account email for display
  const selectedAccountEmail = useMemo((): string => {
    if (!connectedAccounts || connectedAccounts.length === 0) {
      return '';
    }
    if (selectedAccountId) {
      const account = connectedAccounts.find(a => a.id === selectedAccountId);
      if (account) {
        return account.email;
      }
    }
    // Default to primary account
    const primary = connectedAccounts.find(a => a.is_primary);
    return primary?.email || connectedAccounts[0]?.email || '';
  }, [connectedAccounts, selectedAccountId]);

  // Debounce timer for header auto-save
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Snapshot of header values set by applyExternalContent. When the header
  // autosave effect runs and the current values match this snapshot, the run
  // is the echo of an external application — not a user edit — and must not
  // emit onContentChange (it would register a phantom save). Snapshot-compare
  // (rather than a boolean skip-one flag) stays correct even if React bails
  // out of the re-render because every value was unchanged.
  const suppressHeaderSaveRef = useRef<string | null>(null);

  // Track settling window after content loads to suppress normalization-only changes
  const settlingUntilRef = useRef<number>(0);
  const baselineHtmlRef = useRef('');

  // Track if component has mounted to skip initial autosave
  const isInitialMountRef = useRef(true);

  // Initialize settling window to suppress normalization-only changes for 3 seconds
  useEffect(() => {
    settlingUntilRef.current = Date.now() + 3000;
    baselineHtmlRef.current = bodyHtml || body || '';
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: initialize settling window once to suppress normalization-only changes after initial load
  }, []);

  const applyReplacedBody = useCallback(
    (html: string) => {
      // Persist the replacement: replaceContent never emits onChange, so this
      // explicit onContentChange is the ONLY thing that saves the new body.
      // `body` is the text/plain alternative and must NOT be the HTML
      // string: it is what a plain-text client renders and what the send
      // path puts in the text/plain MIME part, so storing markup there
      // ships `<p>` tags to recipients.
      onContentChange({ body_html: html, body: htmlToPlainText(html) });
      // Re-open a settling window against the replaced content so the
      // editor's normalization pass doesn't fire a phantom save. The
      // authored-in-body flag must clear with it: it short-circuits the
      // suppression, so leaving it set from an earlier typing session would
      // make this re-opened window a no-op. The user has not typed in THIS
      // content yet, which is exactly what the flag is supposed to mean.
      settlingUntilRef.current = Date.now() + 3000;
      baselineHtmlRef.current = html;
      userTypedInBodyRef.current = false;
      // In-place replacement — never remount (a key-bump would destroy DOM,
      // focus, scroll position, and cursor).
      plateRef.current?.replaceContent(html);
    },
    [onContentChange]
  );

  const handleInsertCannedResponse = useCallback(
    (html: string) => {
      // Applying a template clears Plate's internal history — Ctrl+Z cannot
      // recover the previous body. Capture it and offer an explicit undo.
      const previousBody = bodyHtml || body || '';
      applyReplacedBody(html);
      toast('Template applied', {
        action: {
          label: 'Undo',
          onClick: () => applyReplacedBody(previousBody),
        },
      });
    },
    [applyReplacedBody, bodyHtml, body]
  );

  // Trigger save when header fields change (skip initial render)
  useEffect(() => {
    // Skip the first render to avoid saving initial state
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }

    // Skip the echo of an applyExternalContent state batch — external
    // content must not round-trip back out as a user edit.
    const currentSnapshot = JSON.stringify({
      to: localTo,
      cc: localCc,
      bcc: localBcc,
      subject: localSubject,
    });
    if (suppressHeaderSaveRef.current === currentSnapshot) {
      suppressHeaderSaveRef.current = null;
      return;
    }
    // Any real change invalidates a stale suppression snapshot.
    suppressHeaderSaveRef.current = null;

    // Debounce the save
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      onContentChange({
        to: localTo,
        cc: localCc.length > 0 ? localCc : undefined,
        bcc: localBcc.length > 0 ? localBcc : undefined,
        subject: localSubject,
      });
    }, 500);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [localTo, localCc, localBcc, localSubject, onContentChange]);

  // Expose a synchronous flush so a consumer (e.g. the compose window's Send
  // button) can pull the latest header values before the 500ms debounce fires,
  // avoiding dispatching stale recipients/subject on an immediate send.
  useImperativeHandle(
    ref,
    () => ({
      flushHeaders: () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = null;
        }
        const snapshot: EmailHeaderSnapshot = {
          to: localTo,
          cc: localCc.length > 0 ? localCc : undefined,
          bcc: localBcc.length > 0 ? localBcc : undefined,
          subject: localSubject,
        };
        // Push into the parent too so its state (and any re-validation) catches
        // up if the send does not immediately close the window.
        onContentChange(snapshot);
        return snapshot;
      },
      isUserEditing: isUserEditingNow,
      applyExternalContent: (content: ExternalEmailContent) => {
        const nextTo = content.to ?? [];
        const nextCc = content.cc ?? [];
        const nextBcc = content.bcc ?? [];
        const nextSubject = content.subject ?? '';
        // Arm the header-autosave suppression BEFORE the state batch so the
        // resulting effect run is recognized as an echo, not a user edit.
        suppressHeaderSaveRef.current = JSON.stringify({
          to: nextTo,
          cc: nextCc,
          bcc: nextBcc,
          subject: nextSubject,
        });
        setLocalTo(nextTo);
        setLocalCc(nextCc);
        setLocalBcc(nextBcc);
        setLocalSubject(nextSubject);
        // Recompute Cc/Bcc visibility from the new values (the old remount
        // path re-initialized these for free; the in-place path must too).
        setShowCc(nextCc.length > 0);
        setShowBcc(nextBcc.length > 0);

        const html = content.body_html ?? content.body ?? '';
        // Re-open the settling window so the editor's normalization pass on
        // the applied content doesn't fire a phantom save. The
        // authored-in-body flag must clear with it: it short-circuits the
        // suppression, so leaving it set from an earlier typing session
        // would make this re-opened window a no-op. Safe because the parent
        // only applies external content when `isUserEditing()` is false —
        // the user is not mid-keystroke in this body.
        settlingUntilRef.current = Date.now() + 3000;
        baselineHtmlRef.current = html;
        userTypedInBodyRef.current = false;
        plateRef.current?.replaceContent(html);
      },
      removeSignature: () => plateRef.current?.removeSignature() ?? false,
    }),
    [
      localTo,
      localCc,
      localBcc,
      localSubject,
      onContentChange,
      isUserEditingNow,
    ]
  );

  // Body change handler for PlateEmailEditor (settling-window suppression of
  // normalization-only diffs, then propagate real edits to the parent).
  const handleBodyChange = useCallback(
    (html: string) => {
      const now = Date.now();
      const inSettlingWindow = now < settlingUntilRef.current;

      // Only update state if HTML actually changed
      if (html !== bodyHtml) {
        // Check if we're still in the settling window after initial content load.
        // Once the user has typed, every change is theirs — skip the check
        // entirely. The text comparison below can only see VISIBLE TEXT, so
        // it used to swallow genuine markup-only edits made inside the
        // window (Space and Enter collapse to whitespace, and bold/italic/
        // link/list/indent/image change no text at all). Those edits never
        // reached onContentChange, so they never became pending changes and
        // survived only in Plate's in-memory value until some later
        // text-changing keystroke happened to re-serialize the body.
        if (inSettlingWindow && !userTypedInBodyRef.current) {
          const normalizeText = (htmlStr: string) => {
            // Replace <br> tags with spaces so line breaks are treated as
            // word separators, then extract the visible text. Sanitize with
            // DOMPurify BEFORE handing the string to the HTML parser:
            // DOMPurify strips event handlers and dangerous constructs and is
            // the barrier that keeps this off the "DOM text reinterpreted as
            // HTML" path (CodeQL js/xss-through-dom). The parsed tree stays
            // detached and we only ever read its textContent, but sanitizing
            // keeps the sink safe regardless. Mirrors the established pattern
            // in lib/crm/utils/memoContentPreview.ts.
            const withBreaks = htmlStr.replace(/<br\s*\/?>/gi, ' ');
            const text =
              new DOMParser().parseFromString(
                DOMPurify.sanitize(withBreaks),
                'text/html'
              ).body.textContent ?? '';
            return text.trim().replace(/\s+/g, ' ');
          };

          const newText = normalizeText(html);
          const baselineText = normalizeText(baselineHtmlRef.current);

          if (newText === baselineText) {
            // Normalization-only change within settling window — suppress
            return;
          }
          // Real edit detected — end settling window early
          settlingUntilRef.current = 0;
        } else if (inSettlingWindow) {
          // User-authored change inside the window — close it early so the
          // rest of the session takes the plain path.
          settlingUntilRef.current = 0;
        }

        // Real edit (text changed) or after settling window - trigger save.
        // `body` is the text/plain alternative and must NOT be the HTML
        // string: it is what a plain-text client renders and what the send
        // path puts in the text/plain MIME part, so storing markup there
        // ships `<p>` tags to recipients.
        onContentChange({
          body_html: html,
          body: htmlToPlainText(html),
        });
      }
    },
    [bodyHtml, onContentChange]
  );

  // Collapse empty Cc/Bcc fields when entering edit mode
  useEffect(() => {
    if (isEditingHeader) {
      if (localCc.length === 0) {
        setShowCc(false);
      }
      if (localBcc.length === 0) {
        setShowBcc(false);
      }
    }
  }, [isEditingHeader, localCc.length, localBcc.length]);

  // Cleanup announcement timer on unmount
  useEffect(() => {
    return () => {
      if (announcementTimerRef.current) {
        clearTimeout(announcementTimerRef.current);
      }
    };
  }, []);

  // Escape exits the email-editor context (see useEscapeToBlur for the
  // layering contract). Disabled while header editing so that press is
  // claimed by the header's own document-level close handler; the
  // image-size menu portals OUTSIDE this container and keeps its own
  // document-level close untouched.
  const handleEditorEscape = useEscapeToBlur(containerRef, isEditingHeader);

  // Handle click outside to close editing
  useEffect(() => {
    if (!isEditingHeader) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (shouldIgnoreOutsideInteraction(event.target)) {
        return;
      }
      if (
        headerRef.current &&
        !headerRef.current.contains(event.target as Node)
      ) {
        if (localTo.length > 0) {
          setIsEditingHeader(false);
        }
      }
    };

    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && localTo.length > 0) {
        setIsEditingHeader(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscKey);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isEditingHeader, localTo.length]);

  // Preview composes through the send path, whose EmailMessage validators require
  // a non-empty recipient list, subject, and plain-text body. Gate the control on
  // the same rules rather than opening a modal that can only render an error —
  // headers come from live local state so the 500ms propagation debounce doesn't
  // leave the button stale, and `body` is the same value the preview payload posts.
  const previewBlockedReason =
    localTo.length === 0 || !localSubject.trim() || !(body ?? '').trim()
      ? 'Add a recipient, a subject, and a message to preview'
      : undefined;

  // Declared once so both branches of the body wrapper below render the
  // SAME element — keeps children identity stable so Plate is not
  // re-rendered when ResizableEmailBody's height state changes on drag.
  const plateEditor = (
    <PlateEmailEditor
      ref={plateRef}
      value={bodyHtml || body || ''}
      onChange={handleBodyChange}
      placeholder='Enter email content'
      readOnly={!editable}
      orgId={orgId}
      onAttachFile={onAttachFile}
      isAttaching={isAttaching}
      resolvedSlots={resolvedSlots}
      fillHeight={embedded}
      toolbarTrailingContent={
        editable && orgId !== undefined ? (
          <CannedResponsesMenu
            orgId={orgId}
            currentBody={bodyHtml || body || ''}
            onInsert={handleInsertCannedResponse}
          />
        ) : undefined
      }
    />
  );

  return (
    <div
      ref={containerRef}
      data-action-card
      className={
        embedded
          ? // Embedded (the floating composer) sits in a `flex-1 min-h-0`
            // parent with a definite height, so become a flex column and
            // claim it — that lets the body region below stretch instead of
            // hugging its text. Card mode (proposal/action cards) keeps
            // sizing to content.
            'relative bg-transparent flex flex-col h-full'
          : 'relative mt-2 bg-white dark:bg-bg-secondary rounded-xl border border-primary shadow-lg dark:shadow-lg'
      }
      onKeyDown={handleEditorEscape}
      onKeyDownCapture={markUserInput}
      onPointerDownCapture={markUserInput}
      onInputCapture={markUserInput}
      onFocusCapture={handleContainerFocus}
      onBlurCapture={handleContainerBlur}
    >
      {/* Email Header - Editable */}
      <div className='relative px-5 py-3'>
        {/* Top-right badges: Version History + Gmail */}
        <div
          className={`absolute right-5 top-3 flex items-center gap-2 ${isEditingHeader ? 'hidden sm:flex' : ''}`}
        >
          {/* Preview Button */}
          {onPreview && (
            <Badge
              size='sm'
              onClick={onPreview}
              isDisabled={previewBlockedReason !== undefined}
              // Accessible name stays stable so the control is findable in
              // both states; the reason it's unavailable rides on the tooltip.
              aria-label='Preview email'
              title={previewBlockedReason ?? 'Preview email'}
            >
              Preview
            </Badge>
          )}

          {/* Version History Button */}
          {versionCount !== undefined &&
            versionCount > 1 &&
            onShowVersionHistory && (
              <Badge
                size='sm'
                className='gap-1'
                onClick={onShowVersionHistory}
                aria-label='View version history'
                title='View version history'
              >
                <svg
                  className='w-3 h-3'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  viewBox='0 0 24 24'
                >
                  <circle cx='12' cy='12' r='10' />
                  <polyline points='12 6 12 12 16 14' />
                </svg>
                History ({versionCount - 1})
              </Badge>
            )}

          {/* Gmail Badge */}
          {showGmailBadge &&
            !embedded &&
            (threadId && onViewThread ? (
              <Badge
                size='sm'
                className='gap-1'
                onClick={onViewThread}
                aria-label='View email thread'
                title='View email thread'
              >
                <svg
                  className='w-3 h-3'
                  viewBox='52 42 88 66'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    fill='#4285f4'
                    d='M58 108h14V74L52 59v43c0 3.32 2.69 6 6 6'
                  />
                  <path
                    fill='#34a853'
                    d='M120 108h14c3.32 0 6-2.69 6-6V59l-20 15'
                  />
                  <path
                    fill='#fbbc04'
                    d='M120 48v26l20-15v-8c0-7.42-8.47-11.65-14.4-7.2'
                  />
                  <path fill='#ea4335' d='M72 74V48l24 18 24-18v26L96 92' />
                  <path
                    fill='#c5221f'
                    d='M52 51v8l20 15V48l-5.6-4.2c-5.94-4.45-14.4-.22-14.4 7.2'
                  />
                </svg>
                View thread
              </Badge>
            ) : (
              <Badge size='sm' className='justify-center'>
                <svg
                  className='w-3 h-3'
                  viewBox='52 42 88 66'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    fill='#4285f4'
                    d='M58 108h14V74L52 59v43c0 3.32 2.69 6 6 6'
                  />
                  <path
                    fill='#34a853'
                    d='M120 108h14c3.32 0 6-2.69 6-6V59l-20 15'
                  />
                  <path
                    fill='#fbbc04'
                    d='M120 48v26l20-15v-8c0-7.42-8.47-11.65-14.4-7.2'
                  />
                  <path fill='#ea4335' d='M72 74V48l24 18 24-18v26L96 92' />
                  <path
                    fill='#c5221f'
                    d='M52 51v8l20 15V48l-5.6-4.2c-5.94-4.45-14.4-.22-14.4 7.2'
                  />
                </svg>
                <span className='ml-1'>Gmail</span>
              </Badge>
            ))}
        </div>

        {!isEditingHeader && editable && (
          /* Collapsed View - Clickable */
          <div
            onClick={() => setIsEditingHeader(true)}
            className='space-y-2 pr-24 cursor-pointer select-none hover:bg-secondary -mx-4 px-4 -my-3 py-3 rounded-t-xl transition-colors overflow-hidden'
          >
            {showFromSelector && (
              <div className='flex items-baseline gap-2 min-w-0'>
                <span className='text-sm text-secondary truncate'>
                  From: {selectedAccountEmail}
                </span>
              </div>
            )}
            <div className='flex items-baseline gap-2 min-w-0'>
              <span className='font-medium text-sm text-primary truncate'>
                To: {renderToAddresses(localTo)}
              </span>
            </div>

            {showCc && localCc.length > 0 && (
              <div className='flex items-baseline gap-2 min-w-0'>
                <span className='text-sm text-secondary truncate'>
                  CC: {formatEmailAddresses(localCc)}
                </span>
              </div>
            )}

            {showBcc && localBcc.length > 0 && (
              <div className='flex items-baseline gap-2 min-w-0'>
                <span className='text-sm text-secondary truncate'>
                  BCC: {formatEmailAddresses(localBcc)}
                </span>
              </div>
            )}

            {localSubject && (
              <div className='flex items-baseline gap-2 min-w-0'>
                <span className='font-medium text-sm text-primary truncate'>
                  Subject: {localSubject}
                </span>
              </div>
            )}
          </div>
        )}
        {isEditingHeader && (
          /* Expanded View - Editable - full width on mobile, leave space for Gmail badge on sm+ */
          <div ref={headerRef} className='space-y-3 sm:pr-24'>
            {/* Aria-live region for duplicate prevention feedback */}
            <div aria-live='polite' className='sr-only'>
              {announcement}
            </div>

            {showFromSelector && connectedAccounts && (
              <div className='flex items-center gap-1.5'>
                <span className='text-sm font-medium text-secondary shrink-0'>
                  From:
                </span>
                <select
                  value={
                    selectedAccountId ||
                    connectedAccounts.find(a => a.is_primary)?.id ||
                    connectedAccounts[0]?.id
                  }
                  onChange={e => {
                    const newAccountId = parseInt(e.target.value, 10);
                    onFromChange?.(newAccountId);
                  }}
                  autoComplete='off'
                  className='flex-1 px-2 py-1.5 text-sm text-base-content bg-base-100 border border-base-300 rounded-lg focus:ring-2 focus:ring-brand-solid focus:border-brand-solid outline-none transition-all cursor-pointer'
                  disabled={!editable}
                >
                  {connectedAccounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.email}
                      {account.is_primary ? ' (Primary)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <DndContext
              sensors={editable ? sensors : undefined}
              collisionDetection={customCollisionDetection}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
              accessibility={{ announcements: dragAnnouncements }}
              measuring={{ droppable: { measure: getClientRect } }}
            >
              <EmailRecipientsInput
                recipients={localTo}
                onChange={setLocalTo}
                label='To'
                placeholder='Add recipients'
                disabled={!editable}
                fieldId='to'
                orgId={orgId}
                linkRecipients={linkRecipientsToRecords}
                isDropTarget={overField === 'to'}
                isEmpty={localTo.length === 0}
                isDragActive={activeEmail !== null}
              />

              {showCc && (
                <EmailRecipientsInput
                  recipients={localCc}
                  onChange={setLocalCc}
                  label='Cc'
                  placeholder='Add Cc recipients'
                  disabled={!editable}
                  fieldId='cc'
                  orgId={orgId}
                  linkRecipients={linkRecipientsToRecords}
                  isDropTarget={overField === 'cc'}
                  isEmpty={localCc.length === 0}
                  isDragActive={activeEmail !== null}
                />
              )}

              {showBcc && (
                <EmailRecipientsInput
                  recipients={localBcc}
                  onChange={setLocalBcc}
                  label='Bcc'
                  placeholder='Add Bcc recipients'
                  disabled={!editable}
                  fieldId='bcc'
                  orgId={orgId}
                  linkRecipients={linkRecipientsToRecords}
                  isDropTarget={overField === 'bcc'}
                  isEmpty={localBcc.length === 0}
                  isDragActive={activeEmail !== null}
                />
              )}

              <DragOverlay>
                {activeEmail ? (
                  <div className='inline-flex items-center gap-1 px-2 py-0.5 bg-secondary text-sm text-secondary rounded-md shadow-lg cursor-grabbing'>
                    <span>{activeEmail}</span>
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>

            <div className='flex items-center gap-3'>
              {!showCc && !activeEmail && (
                <button
                  onClick={() => setShowCc(true)}
                  className='text-sm text-utility-brand-600 hover:text-utility-brand-700 font-medium'
                >
                  Cc
                </button>
              )}
              {!showBcc && !activeEmail && (
                <button
                  onClick={() => setShowBcc(true)}
                  className='text-sm text-utility-brand-600 hover:text-utility-brand-700 font-medium'
                >
                  Bcc
                </button>
              )}
            </div>

            <div className='flex items-center gap-2'>
              <span className='text-sm font-medium text-secondary min-w-[40px]'>
                Subject:
              </span>
              <input
                type='text'
                value={localSubject}
                onChange={e => setLocalSubject(e.target.value)}
                placeholder='Email subject'
                autoComplete='off'
                className='flex-1 px-2 py-1.5 text-sm text-base-content bg-base-100 border border-base-300 rounded-lg focus:ring-2 focus:ring-brand-solid focus:border-brand-solid outline-none transition-all'
                disabled={!editable}
              />
            </div>
          </div>
        )}
        {!isEditingHeader && !editable && (
          /* Read-only View (not editable) */
          <div className='space-y-2 pr-24'>
            {showFromSelector && (
              <div className='flex items-baseline gap-2'>
                <span className='text-sm text-secondary'>
                  From: {selectedAccountEmail}
                </span>
              </div>
            )}
            <div className='flex items-baseline gap-2'>
              <span className='font-medium text-sm text-primary'>
                To: {renderToAddresses(emailDetails?.to_addresses || to)}
              </span>
            </div>

            {hasEmailAddresses(emailDetails?.cc_addresses, cc) && (
              <div className='flex items-baseline gap-2'>
                <span className='text-sm text-secondary'>
                  CC: {formatEmailAddresses(emailDetails?.cc_addresses || cc)}
                </span>
              </div>
            )}

            {hasEmailAddresses(emailDetails?.bcc_addresses, bcc) && (
              <div className='flex items-baseline gap-2'>
                <span className='text-sm text-secondary'>
                  BCC:{' '}
                  {formatEmailAddresses(emailDetails?.bcc_addresses || bcc)}
                </span>
              </div>
            )}

            {subject && (
              <div className='flex items-baseline gap-2'>
                <span className='font-medium text-sm text-primary'>
                  Subject: {subject}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Drafted Message/Reply Badge - hidden when embedded (accordion header
            takes this role). */}
      {!embedded && (
        <div
          data-drafted-badge
          className='z-10 flex items-center gap-1 px-1.5 py-0.5 bg-[#eff8ff] dark:bg-[#1e3a5f] border border-[#b2ddff] dark:border-[#2e5a8a] rounded-md'
        >
          <Stars01 className='w-3 h-3 text-[#2e90fa] dark:text-[#60a5fa]' />
          <span className='text-xs font-medium text-[#175cd3] dark:text-[#60a5fa]'>
            {isReply ? 'Drafted Reply' : 'Drafted Message'}
          </span>
        </div>
      )}

      {/* Divider line with save status */}
      <div className='flex items-center justify-between h-px bg-border-primary relative'>
        {/* Save Status - Right side of divider */}
        {editable && saveStatus !== 'idle' && (
          <div className='absolute right-0 -top-2 z-20 bg-base-100 px-2 text-[10px]'>
            {saveStatus === 'pending' && (
              <span className='text-tertiary flex items-center gap-1'>
                <span className='w-1.5 h-1.5 rounded-full bg-utility-gray-400' />
                <span className='text-[10px]'>Edited</span>
              </span>
            )}
            {saveStatus === 'saving' && (
              <span className='text-utility-brand-600 flex items-center gap-1'>
                <LoadingIndicator size='2xs' inline />
                <span className='text-[10px]'>Saving…</span>
              </span>
            )}
            {saveStatus === 'saved' && (
              <span className='text-utility-success-600 flex items-center gap-1'>
                <svg
                  className='w-3 h-3'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M5 13l4 4L19 7'
                  />
                </svg>
                Saved
              </span>
            )}
            {saveStatus === 'error' && (
              <span className='text-utility-error-600 flex items-center gap-1'>
                <svg
                  className='w-3 h-3'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                  />
                </svg>
                Error saving
              </span>
            )}
          </div>
        )}
      </div>

      {/* Email Body - Seamlessly integrated editor.
            When embedded, this slot claims the remaining height (flex-1) and
            PlateEmailEditor's `fillHeight` stretches the contenteditable to
            cover it. Without both halves the typing area hugs its text and a
            click in the empty space below the last line hits a dead region
            instead of focusing the editor. */}
      {/* Scoped to the BODY: only authorship here may let a body change
            past the settling guard. Typing in a header must not, or an async
            body emission (e.g. the inline-image URL resolution pass, which
            re-emits onChange once uploads resolve) would ride through as a
            user edit and persist resolved URLs over the file: placeholders. */}
      {maxBodyHeight ? (
        <ResizableEmailBody
          maxBodyHeight={maxBodyHeight}
          embedded={embedded}
          onKeyDownCapture={markUserTyped}
          onInputCapture={markUserTyped}
        >
          {plateEditor}
        </ResizableEmailBody>
      ) : (
        <div
          className={cx('relative', embedded && 'flex-1 min-h-[120px]')}
          onKeyDownCapture={markUserTyped}
          onInputCapture={markUserTyped}
        >
          {plateEditor}
        </div>
      )}
    </div>
  );
}

export default EmailEditor;
